import { Button } from 'shared/components/ui';
import React, { useMemo } from 'react';

import { Search, Clear } from '@mui/icons-material';

const FiltrosBusqueda = ({
  filtros,
  onFiltroChange,
  onBuscar,
  onLimpiar,
  centros,
  tiposEstudios,
  estadosTurno,
  procesadores,
  categorias,
  obrasSociales,
  equipos,
  equiposCPAP,
  loading,
  ocultarProcesador = false,
}) => {
  const isFilterActive = (value) => {
    return value !== '' && value !== null && value !== undefined && value !== false;
  };

  // Cuando el centro es "Todos" (vacío), agrupar equipos por nombre
  // para evitar duplicados y filtrar por todos los centros a la vez
  const equiposFiltrados = useMemo(() => {
    if (filtros.centro) {
      // Si hay un centro seleccionado, mostrar solo los de ese centro
      return equipos.filter(eq => String(eq.idCentro) === String(filtros.centro));
    }
    // Si es "Todos los centros", agrupar por nombre
    const agrupados = {};
    equipos.forEach(eq => {
      const nombre = eq.equipo;
      if (!agrupados[nombre]) {
        agrupados[nombre] = { nombre, ids: [] };
      }
      agrupados[nombre].ids.push(eq.idEquipo);
    });
    return Object.values(agrupados).map(g => ({
      idEquipo: g.ids.join(','),
      equipo: g.nombre,
    }));
  }, [equipos, filtros.centro]);

  const equiposCPAPFiltrados = useMemo(() => {
    if (filtros.centro) {
      return equiposCPAP.filter(eq => String(eq.idCentro) === String(filtros.centro));
    }
    const agrupados = {};
    equiposCPAP.forEach(eq => {
      const nombre = eq.equipo;
      if (!agrupados[nombre]) {
        agrupados[nombre] = { nombre, ids: [] };
      }
      agrupados[nombre].ids.push(eq.idEquipo);
    });
    return Object.values(agrupados).map(g => ({
      idEquipo: g.ids.join(','),
      equipo: g.nombre,
    }));
  }, [equiposCPAP, filtros.centro]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Solo limpiar espacios al inicio para nombre, permitir espacios después de la primera palabra
    if (name === 'nombre') {
      const cleanedValue = value.replace(/^\s+/, '');
      e.target.value = cleanedValue;
    }
    // Limpiar todos los espacios para idHorario
    if (name === 'idHorario') {
      const trimmedValue = value.trim();
      e.target.value = trimmedValue;
    }
    // Al cambiar centro, limpiar selección de equipos porque cambian las opciones
    if (name === 'centro') {
      onFiltroChange({ target: { name: 'equipo', value: '' } });
      onFiltroChange({ target: { name: 'equipoCPAP', value: '' } });
    }
    onFiltroChange(e);
  };

  return (
    <div className="filters-card">
      <div className="filters-header">
        <h3 className="filters-title">Filtros de Búsqueda</h3>
      </div>
      <div className="filters-content">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Fecha Desde</label>
            <input
              type="date"
              name="fechaDesde"
              value={filtros.fechaDesde}
              onChange={onFiltroChange}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Fecha Hasta</label>
            <input
              type="date"
              name="fechaHasta"
              value={filtros.fechaHasta}
              onChange={onFiltroChange}
              className="filter-input"
            />
          </div>
          <div className={`filter-group ${isFilterActive(filtros.centro) ? 'filter-active' : ''}`}>
            <label className="filter-label">Centro</label>
            <select
              name="centro"
              value={filtros.centro}
              onChange={handleInputChange}
              className="filter-select"
            >
              <option value="">Todos los centros</option>
              {centros.map(centro => (
                <option key={centro.idCentro} value={centro.idCentro}>
                  {centro.centro}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.tipoEstudio) ? 'filter-active' : ''}`}>
            <label className="filter-label">Tipo de Estudio</label>
            <select
              name="tipoEstudio"
              value={filtros.tipoEstudio}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los estudios</option>
              {tiposEstudios.map(tipo => (
                <option key={tipo.idTipoEstudio} value={tipo.idTipoEstudio}>
                  {tipo.tipoEstudio}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.estadoTurno) ? 'filter-active' : ''}`}>
            <label className="filter-label">Estado</label>
            <select
              name="estadoTurno"
              value={filtros.estadoTurno}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los estados</option>
              {estadosTurno.map(estado => (
                <option key={estado.idEstadoTurno} value={estado.idEstadoTurno}>
                  {estado.estadoTurno}
                </option>
              ))}
            </select>
          </div>
          {!ocultarProcesador && (
          <div className={`filter-group ${isFilterActive(filtros.procesador) ? 'filter-active' : ''}`}>
            <label className="filter-label">Procesador</label>
            <select
              name="procesador"
              value={filtros.procesador}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los procesadores</option>
              {procesadores.map(proc => (
                <option key={proc.id} value={proc.id}>
                  {proc.usuario1}
                </option>
              ))}
            </select>
          </div>
          )}
          <div className={`filter-group ${isFilterActive(filtros.categoria) ? 'filter-active' : ''}`}>
            <label className="filter-label">Categoría</label>
            <select
              name="categoria"
              value={filtros.categoria}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todas las categorías</option>
              {categorias.map(cat => (
                <option key={cat.idCategoria} value={cat.idCategoria}>
                  {cat.categoria1}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.obraSocial) ? 'filter-active' : ''}`}>
            <label className="filter-label">Obra Social</label>
            <select
              name="obraSocial"
              value={filtros.obraSocial}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todas las obras sociales</option>
              {obrasSociales.map(os => (
                <option key={os.idObraSocial} value={os.idObraSocial}>
                  {os.obraSocial}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.equipo) ? 'filter-active' : ''}`}>
            <label className="filter-label">Equipo</label>
            <select
              name="equipo"
              value={filtros.equipo}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los equipos</option>
              {equiposFiltrados.map((eq, index) => (
                <option key={eq.idEquipo || index} value={eq.idEquipo}>
                  {eq.equipo}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.equipoCPAP) ? 'filter-active' : ''}`}>
            <label className="filter-label">Equipo CPAP</label>
            <select
              name="equipoCPAP"
              value={filtros.equipoCPAP}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los equipos CPAP</option>
              {equiposCPAPFiltrados.map((cpap, index) => (
                <option key={cpap.idEquipo || index} value={cpap.idEquipo}>
                  {cpap.equipo}
                </option>
              ))}
            </select>
          </div>
          <div className={`filter-group ${isFilterActive(filtros.canales) ? 'filter-active' : ''}`}>
            <label className="filter-label">Canales</label>
            <select
              name="canales"
              value={filtros.canales}
              onChange={onFiltroChange}
              className="filter-select"
            >
              <option value="">Todos los canales</option>
              <option value="0">0</option>
              <option value="4">4</option>
              <option value="21">21</option>
            </select>
          </div>
        </div>
        <div className="filters-row">
          <div className={`filter-group ${isFilterActive(filtros.nombre) ? 'filter-active' : ''}`}>
            <label className="filter-label">Nombre/DNI</label>
            <input
              type="text"
              name="nombre"
              value={filtros.nombre}
              onChange={handleInputChange}
              placeholder="Buscar por nombre o DNI"
              className="filter-input"
            />
          </div>
          <div className={`filter-group ${isFilterActive(filtros.idHorario) ? 'filter-active' : ''}`}>
            <label className="filter-label">ID Turno</label>
            <input
              type="text"
              name="idHorario"
              value={filtros.idHorario}
              onChange={handleInputChange}
              placeholder="ID del turno"
              className="filter-input"
            />
          </div>
          <div className={`filter-group ${isFilterActive(filtros.mostrarEnviados) ? 'filter-active' : ''}`}>
            <label className="filter-label">Opciones</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="mostrarEnviados"
                  checked={filtros.mostrarEnviados}
                  onChange={onFiltroChange}
                />
                <span>Mostrar enviados</span>
              </label>
            </div>
          </div>
          <div className="filter-actions">
            <Button onClick={onBuscar} disabled={loading} className="btn-search">
              <Search sx={{ fontSize: 18, mr: 0.5 }} />
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button onClick={onLimpiar} className="btn-clear">
              <Clear sx={{ fontSize: 18, mr: 0.5 }} />
              Limpiar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltrosBusqueda;
